import React, { useRef, useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from "@/components/ui/dialog";
import { X } from "lucide-react";

/**
 * Lazy loading shared logic
 */
const observerMap = new Map<string, IntersectionObserver>();
const observerCallbacks = new WeakMap<Element, () => void>();

function getSharedObserver(rootMargin: string) {
  if (observerMap.has(rootMargin)) return observerMap.get(rootMargin)!;

  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const cb = observerCallbacks.get(entry.target);
          if (cb) {
            cb();
            observerCallbacks.delete(entry.target);
          }
          observer.unobserve(entry.target);
        }
      }
    },
    { rootMargin },
  );

  observerMap.set(rootMargin, observer);
  return observer;
}

const MAX_CONCURRENT = 6;
let activeCount = 0;
const queue: (() => void)[] = [];

function enqueueLoad(callback: () => void) {
  if (activeCount < MAX_CONCURRENT) {
    activeCount++;
    callback();
  } else {
    queue.push(callback);
  }
}

function releaseSlot() {
  activeCount--;
  if (queue.length > 0 && activeCount < MAX_CONCURRENT) {
    activeCount++;
    const next = queue.shift();
    next?.();
  }
}

function cancelLoad(callback: () => void) {
  const idx = queue.indexOf(callback);
  if (idx > -1) queue.splice(idx, 1);
}

export interface MyImageProps
  extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  className?: string;
  preview?: boolean;
  lazy?: boolean;
  rootMargin?: string;
  previewSrc?: string;
}

/**
 * MyImage - 支持懒加载和点击预览大图的图片组件
 *
 * @param src 图片地址
 * @param alt 图片描述
 * @param preview 是否开启预览功能 (默认: true)
 * @param lazy 是否开启懒加载 (默认: true)
 * @param rootMargin 懒加载触发距离 (默认: '100px')
 * @param previewSrc 预览图地址 (不传则默认使用 src)
 */
export const MyImage = React.forwardRef<HTMLImageElement, MyImageProps>(
  (
    {
      src,
      alt,
      className,
      preview = true,
      lazy = true,
      rootMargin = "100px",
      previewSrc,
      onClick,
      ...props
    },
    ref,
  ) => {
    const [open, setOpen] = useState(false);
    const [activeSrc, setActiveSrc] = useState<string | undefined>(
      lazy ? undefined : src,
    );
    const imgRef = useRef<HTMLImageElement | null>(null);
    const loadCallbackRef = useRef<(() => void) | null>(null);
    const slotAcquired = useRef(false);

    // 懒加载逻辑
    const onVisible = useCallback(() => {
      const cb = () => {
        slotAcquired.current = true;
        setActiveSrc(src);
      };
      loadCallbackRef.current = cb;
      enqueueLoad(cb);
    }, [src]);

    const handleComplete = useCallback(() => {
      if (slotAcquired.current) {
        slotAcquired.current = false;
        releaseSlot();
      }
    }, []);

    useEffect(() => {
      if (!lazy) {
        setActiveSrc(src);
        return;
      }

      const el = imgRef.current;
      if (!el) return;

      if (!("IntersectionObserver" in window)) {
        setActiveSrc(src);
        return;
      }

      const observer = getSharedObserver(rootMargin);
      observerCallbacks.set(el, onVisible);
      observer.observe(el);

      return () => {
        observerCallbacks.delete(el);
        observer.unobserve(el);
        if (loadCallbackRef.current) {
          cancelLoad(loadCallbackRef.current);
        }
        if (slotAcquired.current) {
          slotAcquired.current = false;
          releaseSlot();
        }
      };
    }, [src, lazy, rootMargin, onVisible]);

    const handleOpen = useCallback(
      (e: React.MouseEvent<HTMLImageElement>) => {
        if (preview) {
          setOpen(true);
        }
        onClick?.(e);
      },
      [preview, onClick],
    );

    return (
      <>
        <img
          ref={(node) => {
            imgRef.current = node;
            if (typeof ref === "function") ref(node);
            else if (ref)
              (ref as React.MutableRefObject<HTMLImageElement | null>).current =
                node;
          }}
          src={activeSrc}
          alt={alt}
          className={className}
          onClick={handleOpen}
          onLoad={handleComplete}
          onError={handleComplete}
          style={{ cursor: preview ? "zoom-in" : "default", ...props.style }}
          {...props}
        />
        {preview && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent className="p-0 border-none bg-transparent shadow-none w-fit h-fit max-w-[95vw] max-h-[95vh] [&>button]:hidden outline-none">
              <DialogTitle className="sr-only">{alt}</DialogTitle>
              <DialogDescription className="sr-only">
                {alt} 大图预览
              </DialogDescription>
              <div className="relative group">
                <img
                  src={previewSrc || src}
                  alt={alt}
                  className="max-w-[95vw] max-h-[95vh] object-contain rounded-md cursor-pointer"
                  onClick={() => setOpen(false)}
                />
                <DialogClose className="absolute top-2 right-2 rounded-full p-1 bg-background/80 backdrop-blur-sm border shadow-sm hover:bg-accent transition-colors">
                  <X className="h-4 w-4" />
                  <span className="sr-only">关闭</span>
                </DialogClose>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  },
);

MyImage.displayName = "MyImage";
